export function stripHtml(html: string) {
  return html.replace(/<[^>]*>?/gm, "");
}

/**
 * Decodes common HTML entities to their character equivalents
 */
export function decodeHtmlEntities(html: string): string {
  return (
    html
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      // Decode numeric HTML entities (e.g., &#8217; → ')
      .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec))
  );
}

export function extractTextFromHtml(html: string): string {
  return html
    .replace(/<[^>]*>?/gm, (match, offset) => {
      const beforeChar = offset > 0 ? html[offset - 1] : "";
      const afterChar =
        offset + match.length < html.length ? html[offset + match.length] : "";
      if (
        beforeChar &&
        afterChar &&
        !/\s/.test(beforeChar) &&
        !/\s/.test(afterChar)
      ) {
        return " ";
      }
      return "";
    })
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Replaces occurrences of &quot; that are not
 * between angle brackets with the character `"`.
 *
 * Quotation marks inside attribute values need to be escaped to
 * avoid ending an attribute early, but quotation marks that aren't
 * inside attribute values don't need to be escaped for any reason.
 */
export function unescapeNonAttributeQuotes(html: string): string {
  let ret = "";
  let insideAngleBrackets = false;
  /** " or ' if we are in a string, null otherwise */
  let delimiterForCurrentString: "'" | '"' | null = null;
  let i = 0;
  const toReplace = "&quot;";
  function setInsideAngleBrackets(newVal: boolean) {
    if (insideAngleBrackets && newVal) {
      throw new Error(
        `HTML is malformed: Found < inside tag at index ${i}. html: ${html}`,
      );
    }
    insideAngleBrackets = newVal;
  }

  while (i < html.length - toReplace.length) {
    if (
      !insideAngleBrackets &&
      html.slice(i, i + toReplace.length) === toReplace
    ) {
      ret += '"';
      i += toReplace.length;
      continue;
    }
    const c = html[i];

    // Quotation marks outside angle brackets don't delimit strings
    if (insideAngleBrackets && (c === '"' || c === "'")) {
      if (delimiterForCurrentString === c) {
        delimiterForCurrentString = null;
      } else {
        delimiterForCurrentString = c;
      }
    }

    // Angle brackets inside strings don't count
    if (delimiterForCurrentString === null) {
      if (c === "<") {
        setInsideAngleBrackets(true);
      } else if (c === ">") {
        setInsideAngleBrackets(false);
      }
    }
    ret += c;
    i++;
  }
  ret += html.substring(i);

  return ret;
}

export function escapeHtml(html: string): string {
  return html
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
